import Configuration from '@/agilis/util/Configuration'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import FormSelect from '@/agilis/components/Form/FormSelect/FormSelect.vue'
import jsonLogic from '@/plugins/jsonLogic'
import UnStyledLink from '@/agilis/components/UnstyledLink/UnstyledLink.vue'
import L from 'lodash'

// Associations are defined as follows:
// association: {
//   id: associationId,
//   target: {itemDTO},
//   type: associationTypeId
// }

// When the associations are loaded fetchAssociations()
// it is possible to specify a url in the definition of the associationTypes in configuration
// to populate the itemDTO from a query (see sales.trackertypes.order.json for example),
// this allows to show on the data table and by expandRow more data than those provided by the standard search for trackerType,
// via the decorateAssociationData() method.

// To add an association you must first select the type of associations and
// if this specifies targetTrackerTypes a query is executed for each targetTrackerType defined in the configuration
// by the defined url fetchTrackerTypes(),
// if the targetTrackerTypes are not defined a generic query is executed
// for the trackers which the user has permission to in the current context, fetchTrackerGeneric().

// You can expand the associations to see more detail than what is displayed on the table with an 'expandRows' prop,
// in order to expand the association you need to pass a configuration that contains the formRows and columns and the itemDTO.
// The configuration is retrieved via the associationTableConfig() method and is used several times,
// when needed to expand the association it is enriched with the formRows retrieved from associationTypesConfig()
// and the columns from associationColumnsConfig(), this allows you to configure which data to display

function createQueue (tasks, maxNumOfWorkers = 4) {
  let numOfWorkers = 0
  let taskIndex = 0

  // eslint-disable-next-line promise/param-names
  return new Promise(done => {
    const handleResult = index => result => {
      tasks[index] = result
      numOfWorkers--
      getNextTask()
    }
    const getNextTask = () => {
      if (numOfWorkers < maxNumOfWorkers && taskIndex < tasks.length) {
        tasks[taskIndex]().then(handleResult(taskIndex)).catch(handleResult(taskIndex))
        taskIndex++
        numOfWorkers++
        getNextTask()
      } else if (numOfWorkers === 0 && taskIndex === tasks.length) {
        done(tasks)
      }
    }
    getNextTask()
  })
}

export default {
  name: 'TrackerDataTableItemAssociations',
  mixins: [deleteMixin],

  components: {
    UnStyledLink,
    FormSelect
  },

  props: {
    trackerDTO: Object,
    hideActions: Boolean,
    hideToolbar: Boolean,
    showDivider: Boolean
  },

  data () {
    return {
      dialog: false,
      trackerWithAssociations: {},
      operationPost: 'post',
      trackerAssociation:
      {
        sourceTrackerId: '',
        sourceApplicationId: '',
        sourceTrackerTypeId: '',
        targetTrackerId: '',
        targetApplicationId: '',
        targetTrackerTypeId: '',
        type: ''
      },
      trackerAssociationTableConfigDefault: {
        columns: [
          {
            id: 'associationType',
            type: 'text',
            label: 'label.associationType',
            renderMethod: 'renderAssociationType'
          },
          {
            id: 'id',
            type: 'number',
            label: 'label.id'
          },
          {
            id: 'trackerTypeId',
            type: 'string',
            label: 'label.trackerType',
            renderMethod: 'renderTrackerTypeId'
          },
          {
            id: 'state',
            type: 'text',
            label: 'label.status',
            renderMethod: 'renderState'
          },
          {
            id: 'title',
            type: 'text',
            label: 'label.title'
          },
          {
            id: 'assigneeFullName',
            type: 'text',
            label: 'label.owner'
          }
        ]
      },
      trackers: [],
      search: null,
      loading: false,
      select: null
    }
  },

  mounted () {
    if (L.isEmpty(this.menuData)) {
      this.$el.parentElement.style.display = 'none'
    } else {
      this.fetchAssociations()
    }
  },

  computed: {
    ...mapGetters({
      authorizedApplications: 'configurationStore/auth/applications',
      authorizedWorkspaces: 'configurationStore/auth/workspaces',
      authorizedTrackerTypes: 'configurationStore/auth/trackerTypes',
      dataTableById: 'contextStore/trackerDataTableById',
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerType: 'contextStore/trackerType',
      trackerTypes: 'contextStore/trackerTypes',
      trackerTypeIds: 'contextStore/trackerTypeIds',
      trackerId: 'contextStore/trackerId'
    }),
    title () {
      return this.$t('label.associations')
    },
    popupTitle () {
      return this.$t('label.associationsPopup', { trackerType: this.$t(this.trackerType.name), id: this.trackerId })
    },
    associationsColumnsConfig () {
      return this.associationTableConfig()
        ? this.associationTableConfig().columns
        : this.trackerAssociationTableConfigDefault.columns
    },

    associationsHeader () {
      const columns = this.associationsColumnsConfig
      const hh = Object.values(columns).map((c) => {
        return {
          text: this.$t(c.label),
          value: c.id
        }
      })

      return [
        ...hh,
        this.hideActions ? null : { value: 'actions', text: 'Actions' }
      ].filter(Boolean)
    },

    menuData () {
      const associationsType = this.trackerType?.associationTypes
      if (associationsType) {
        return associationsType.map((associationType) => {
          if (this.userCanAccessFeatureAssociation(associationType.id, 'post')) {
            return {
              id: associationType.id,
              label: this.$t(associationType.label),
              targetTrackerTypes: associationType.targetTrackerTypes
            }
          }
        })
      } else {
        return {}
      }
    },
    authTrackerTypesIds () {
      return L.uniq(this.authorizedTrackerTypes[this.applicationId].map(trackerTypes => {
        return Object.keys(trackerTypes)
      }).flat())
    },

    showExpand () {
      return this.associationTableConfig().expandRows
    },

    expandRowsIsOpen () {
      return this.associationTableConfig().expandRowsIsOpen
    },

    expandRowsArray () {
      return this.expandRowsIsOpen ? this.trackerWithAssociations : []
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    associationTableConfig (associationTypeId, item) {
      const associationTableConfiguration =
        L.cloneDeep(Configuration.simpleTable.getAssociationsTable(this.applicationId, this.trackerTypeId))
      if (associationTypeId && item) {
        associationTableConfiguration.formRows = this.associationTypesConfig(associationTypeId, item)?.formRows ||
          this.dataTableById(item.trackerTypeId).formRows
        associationTableConfiguration.columns = this.associationColumnsConfig(associationTypeId, item) || {}
      }
      return associationTableConfiguration
    },

    associationTypesConfig (associationTypeId, item) {
      const associationType = this.trackerType.associationTypes?.find(associationType => associationType.id === associationTypeId)
      return associationType?.targetTrackerTypes?.find(targetTrackerType => targetTrackerType.trackerTypeId === item.trackerTypeId)
    },

    associationColumnsConfig (associationTypeId, item) {
      let columns = L.merge({}, this.associationsColumnsConfig, this.dataTableById(item.trackerTypeId).columns)
      const url = this.associationTypesConfig(associationTypeId, item)?.url || this.associationTypesConfig(associationTypeId, item)?.api?.url
      if (url && url.includes('query')) {
        const columnsQuery =
          Configuration.query.getQueryDatatable(this.applicationId, url.substring(url.lastIndexOf('/') + 1)).columns
        columns = L.merge({}, columns, columnsQuery)
      }
      return columns
    },

    getFilterId (associations) {
      return {
        terms: [
          { field: 'id', operator: 'IN', values: associations.map(association => association.target.id) }
        ]
      }
    },

    decorateAssociationData (_associations) {
      const associationsByTypeAndTrackerType = this.groups(_associations)
      const tasks = [
        // 1 search for each targetTrackerType
        ...Object.values(associationsByTypeAndTrackerType).map(association => {
          const associationConfig = this.associationTypesConfig(association[0].type, association[0].target)
          const api = this.$axios.agilisComputeApiProp(associationConfig, 'POST')
          if (api.url) {
            return () => this.$axios({
              ...api.config,
              method: api.method,
              url: api.url,
              data: Object.assign({}, api.body, this.getFilterId(association)) // replaces the filter term with the filter id
            })
          }
        })
      ].filter(Boolean)
      if (tasks) {
        createQueue(tasks, 1).then(responses => {
          responses.map(response => {
            this.addDataToAssociation(_associations, this.resolveDataItems(response))
          })
        }).catch(error => {
          this.loading = false
          this.notifyError(error)
        })
      }
      return _associations
    },

    groups (items) {
      return items.reduce(function (groups, item) {
        const g = item.type + '-' + item.target.trackerTypeId
        groups[g] = groups[g] || []
        groups[g].push(item)
        return groups
      }, [])
    },

    addDataToAssociation (associations, associationsByQuery) {
      return associations.map(association => {
        if (associationsByQuery.find(associationByQuery => associationByQuery.id === association.target.id)) {
          association.target = L.merge({}, association.target,
            associationsByQuery.find(associationByQuery => associationByQuery.id === association.target.id))
          return association
        }
      })
    },

    fetchAssociations () {
      this.startLoader({ id: 'tracker-association' })
      const url = Vue.axios.agilisUrlTrackerAssociations(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.trackerWithAssociations = response.data
          if (!url.endsWith('V2')) {
            // legacy association api needs decoration with entity data
            this.trackerWithAssociations = this.decorateAssociationData(response.data)
          }
          this.trackerWithAssociations = this.filterAssociationByPrivilege(this.trackerWithAssociations)
          this.$emit('associationsLoaded', this.trackerWithAssociations)
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-association' })
        })
    },

    saveAssociations () {
      this.trackerAssociation.sourceTrackerId = this.trackerId
      this.trackerAssociation.sourceApplicationId = this.applicationId
      this.trackerAssociation.sourceTrackerTypeId = this.trackerTypeId

      this.trackerAssociation.targetTrackerId = this.select.id
      this.trackerAssociation.targetApplicationId = this.select.applicationId
      this.trackerAssociation.targetTrackerTypeId = this.select.trackerTypeId

      this.trackerAssociation.type = this.trackerAssociation.type['id']

      const url = Vue.axios.agilisUrlTrackerAssociations(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      Vue.axios
        .post(
          url,
          this.trackerAssociation,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.fetchAssociations()
        })
        .catch((error) => {
          this.notifyError(error)
        })
      this.closeDialog()
    },

    deleteAssociations (associations) {
      const url = Vue.axios.agilisUrlTrackerAssociationsDelete(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        associations.id)
      Vue.axios
        .delete(
          url,
          {
            ...Vue.axios.agilisRequestConfig(url),
            data: associations
          })
        .then(() => {
          this.fetchAssociations()
        })
        .catch((error) => {
          this.notifyError(error)
        })
    },

    fetchTrackers (targetTrackerTypes) {
      this.loading = true
      targetTrackerTypes ? this.fetchTrackerTypes(targetTrackerTypes) : this.fetchTrackerGeneric()
    },

    fetchTrackerTypes (targetTrackerTypes) {
      const _trackers = []
      const tasks = [
        // 1 search for each targetTrackerType
        ...targetTrackerTypes.map(targetTrackerType => {
          const api = this.$axios.agilisComputeApiProp(targetTrackerType, 'POST')

          if (!api.url) {
            return
          }

          return () => this.$axios({
            ...api.config,
            method: api.method,
            url: api.url,
            data: api.body
          })
        })
      ]
      createQueue(tasks, 1)
        .then(responses => {
          responses.map(response => {
            const targetTrackerType = targetTrackerTypes
              .find(targetTrackerType => {
                if (targetTrackerType.url) {
                  return targetTrackerType.url === response.config.url
                }
                return targetTrackerType.api.url === response.config.url
              })
            // query api returns response.data.items
            let items = this.resolveDataItems(response)
            targetTrackerType.selectTitle
              ? _trackers.push(...this.concatSelectTitle(items, targetTrackerType.selectTitle))
              : _trackers.push(...this.defaultSelectTitle(items))
          })
          this.trackers = _trackers
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.notifyError(error)
        })
    },

    async fetchTrackerGeneric () {
      try {
        const url = '/api/tracker/search'
        const body = {
          filterTerms: [
            { field: 'applicationId', operator: 'EQUALS', value: this.applicationId },
            { field: 'workspaceId', operator: 'EQUALS', value: this.workspaceId },
            { field: 'trackerTypeId', operator: 'IN', values: this.authTrackerTypesIds }
          ]
        }
        const config = this.$axios.agilisRequestConfig(url)
        const response = await this.$axios.post(url, body, config)
        this.trackers = this.defaultSelectTitle(response.data.items)
      } catch (e) {
        this.loadFailure = true
        this.notifyError(e)
      }
    },

    defaultSelectTitle (_trackers) {
      return _trackers.map(item => {
        return Object.assign(item, { fullTitle:
          this.$t(Configuration.getApplication(item.applicationId).trackerTypes[item.trackerTypeId].name) +
          ' ' + item.id +
          ' - ' + item.title
        })
      })
    },

    concatSelectTitle (_trackers, selectTitle) {
      return _trackers.map(item => {
        return Object.assign(item, { fullTitle: jsonLogic.apply(selectTitle.jsonLogic, item)
        })
      })
    },

    handleDeleteItem (associations) {
      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.deleteMessage(this.$t('label.associations'))],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deleteAssociations(associations)
        }
      })
    },

    handleAssociatedClick (associationTracker) {
      if (associationTracker) {
        this.$router.agilis.viewTracker({
          applicationId: associationTracker.applicationId,
          trackerId: associationTracker.id,
          trackerTypeId: associationTracker.trackerTypeId,
          parentTrackerId: this.trackerId,
          workspaceId: associationTracker.workspaceId
        })
      }
    },

    associationsTrackerUrl (associationTracker) {
      return associationTracker ? this.$router.agilis.urlViewTracker({
        applicationId: associationTracker.applicationId,
        trackerId: associationTracker.id,
        trackerTypeId: associationTracker.trackerTypeId,
        workspaceId: associationTracker.workspaceId
      }) : ''
    },

    onDeleteItemDone () {
      this.fetchAssociations()
    },

    renderAssociationsColumn (colId, trackerAssociationOutput) {
      const colConfig = this.associationsColumnsConfig.find(associationColumn => associationColumn.id === colId)
      if (trackerAssociationOutput.target) {
        const targetItem = Object.assign({}, trackerAssociationOutput.target, { associationType: trackerAssociationOutput.type })
        return Configuration.datatable.renderColumn(colConfig, targetItem)
      } else {
        return colId === 'associationType' ? this.$t('label.associationInvalid') : ''
      }
    },

    closeDialog () {
      this.trackerAssociation.type = ''
      this.select = ''
      this.dialog = false
    },

    userCanDo (operation) {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'association', operation, this.trackerDTO, this.userId)
    },

    userCanAccessTargetTracker (association, operation) {
      return Configuration.auth.userCanAccessTargetTracker(this.selectedContext, association, operation, this.trackerDTO, this.userId)
    },

    userCanAccessFeatureAssociation (associationType, operation) {
      return Configuration.auth.userCanAccessFeatureAssociation(
        this.selectedContext, associationType, operation, this.trackerDTO, this.userId)
    },

    onChange (associationType) {
      this.fetchTrackers(associationType.targetTrackerTypes)
    },

    resolveDataItems (response) {
      // tracker api returns data array while query api returns data.items
      return response.data.items ? response.data.items : response.data
    },

    filterAssociationByPrivilege (associations) {
      return associations.filter(association => {
        const associationTypeConfig = this.trackerType.associationTypes?.find(associationType => associationType.id === association.type)
        if (associationTypeConfig?.showWhenTargetNotAccessible || this.userCanAccessTargetTracker(association, 'get')) {
          return association
        }
      })
    }
  }
}
